import { formatShortDate } from 'features/libra/dashboard/ticketDashboard/libra/formatShortDate'
import type {
  Pill,
  PillGroup,
  PillOption,
  PillTone,
} from 'features/libra/dashboard/ticketDashboard/types'
import {
  resolveSignoffRequirements,
  type SignoffRequirement,
} from 'features/libra/signoff/signoffRequirements'
import type {
  SignoffsField,
  SignoffSlot,
} from 'features/libra/signoff/signoffTypes'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'

import { SIGNOFFS_ID } from './categoryIds'

/**
 * In filter-option order.
 * - `awaiting`: required, unsigned, and the ticket's stage allows signing.
 * - `upcoming`: required, but the ticket hasn't reached a signing stage.
 * - `waived`: `requiredWhen` excludes this ticket.
 */
const SLOT_STATES = ['awaiting', 'signed', 'upcoming', 'waived'] as const
export type SlotState = (typeof SLOT_STATES)[number]

/** The tone carries the state; the group's cap carries the pen icon. */
const STATES: Record<SlotState, { label: string; tone?: PillTone }> = {
  awaiting: { label: 'awaiting', tone: 'warning' },
  signed: { label: 'signed', tone: 'success' },
  upcoming: { label: 'not yet signable' },
  waived: { label: 'not required' },
}

const STATE_OPTIONS: ReadonlyArray<PillOption> = SLOT_STATES.map((state) => ({
  value: state,
  label: STATES[state].label,
  tone: STATES[state].tone,
}))

export const SIGNOFFS_GROUP: PillGroup = {
  id: SIGNOFFS_ID,
  label: 'Sign-offs',
  icon: 'PenLine',
}

function signoffCategoryId(slotId: string): string {
  return `signoff_${slotId}`
}

function slotState(
  { required, record }: SignoffRequirement,
  signable: boolean
): SlotState {
  if (record) return 'signed'
  if (!required) return 'waived'
  return signable ? 'awaiting' : 'upcoming'
}

/** Mirrors the ticket page's sign-off card; `slot.name` is its config-written "who signs" line. */
function describe(
  requirement: SignoffRequirement,
  state: SlotState
): string | undefined {
  const { slot, record, previous } = requirement
  switch (state) {
    case 'signed':
      return record
        ? `Signed by ${record.userName} · ${formatShortDate(record.signedAt)}`
        : undefined
    case 'awaiting':
      return [
        'Awaiting signature',
        previous
          ? `Previously signed by ${previous.userName} · ${formatShortDate(previous.signedAt)}`
          : slot.name,
      ]
        .filter(Boolean)
        .join(' · ')
    case 'upcoming':
      return 'Required, but not in review stage'
    case 'waived':
      return 'Not required for this ticket'
  }
}

/**
 * One pill per sign-off slot, in that slot's own category, labelled with the
 * slot's `shortTitle` (else its title). Slots from every workflow are merged
 * by id, in workflow order, so filters and boards see one list.
 */
export function createSignoffPills(workflows: ReadonlyArray<LibraWorkflow>) {
  const slots = new Map<string, SignoffSlot>()
  workflows.forEach((workflow) =>
    workflow.signoffConfig?.signoffs.forEach((slot) => {
      if (!slots.has(slot.id)) slots.set(slot.id, slot)
    })
  )
  const labelOf = (slot: SignoffSlot) => slot.shortTitle ?? slot.title

  return {
    slots: [...slots.values()].map((slot) => ({
      id: slot.id,
      categoryId: signoffCategoryId(slot.id),
      label: labelOf(slot),
    })),
    /** Every slot's category takes the same states. */
    options: STATE_OPTIONS,
    forTicket: (
      ticket: LibraTicket,
      workflow: LibraWorkflow | undefined,
      signoffs: SignoffsField,
      stageId: string
    ): Pill[] => {
      const config = workflow?.signoffConfig
      if (!config) return []
      const signable = config.enabledStages.includes(stageId)
      return resolveSignoffRequirements(
        config.signoffs,
        signoffs,
        ticket.fieldValues
      ).map((requirement) => {
        const state = slotState(requirement, signable)
        const { tone } = STATES[state]
        const label = labelOf(requirement.slot)
        return {
          category: signoffCategoryId(requirement.slot.id),
          value: state,
          label,
          tooltip: `${requirement.slot.title}: ${STATES[state].label}`,
          tone,
          info: {
            title: requirement.slot.title,
            description: describe(requirement, state),
          },
        }
      })
    },
  }
}

export type SignoffPills = ReturnType<typeof createSignoffPills>
